import { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import logoIcon from 'design-system/src/assets/lean-agility-icon.svg';

import { CountdownClock, DarkModeSwitchButton } from '../atoms';
import { useRequireFeatureFlagIfProvided } from '../../hooks';
import type { FeatureFlags } from '../../client';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NavItem {
  label: string;
  to: string;
  end?: boolean;
  featureFlag?: keyof FeatureFlags;
}

export interface NavBarProps {
  roleLabel?: React.ReactNode;
  items: NavItem[];
  exitTo?: string;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function NavItemLink({ item }: { item: NavItem }) {
  const enabled = useRequireFeatureFlagIfProvided(item.featureFlag);
  if (!enabled) return null;

  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          isActive ? 'bg-primary-500 text-white' : 'text-text-secondary hover:bg-bg-subtle hover:text-text-primary'
        }`
      }
    >
      {item.label}
    </NavLink>
  );
}

function MobileNavItemLink({ item, onClick }: { item: NavItem; onClick: () => void }) {
  const enabled = useRequireFeatureFlagIfProvided(item.featureFlag);
  if (!enabled) return null;

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      className={({ isActive }) =>
        `block px-4 py-3 text-sm font-medium transition-colors ${
          isActive ? 'bg-primary-500 text-white' : 'text-text-secondary hover:bg-bg-subtle hover:text-text-primary'
        }`
      }
    >
      {item.label}
    </NavLink>
  );
}

// ─── NavBar ──────────────────────────────────────────────────────────────────

export function NavBar({ roleLabel, items, exitTo = '/' }: NavBarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  // Auto-close dropdown on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-(--z-navbar) h-14 bg-bg-elevated border-b border-border-muted">
        <div className="flex items-center h-full px-4">
          {/* Left: Logo + role */}
          <div className="flex items-center gap-3 shrink-0">
            <Link to="/" className="flex items-center gap-2" aria-label="Home">
              <img src={logoIcon} alt="Lean Agility" className="w-6 h-6" />
              <span className="text-xl font-bold text-gradient hidden sm:inline">Value Flow Game</span>
            </Link>
            {roleLabel && <span className="text-xs text-text-secondary">{roleLabel}</span>}
          </div>

          {/* Center: Nav links (desktop) */}
          <nav className="hidden sm:flex items-center gap-1 absolute left-1/2 -translate-x-1/2">
            {items.map((item) => (
              <NavItemLink key={item.to} item={item} />
            ))}
          </nav>

          {/* Right: Clock + theme + leave + hamburger */}
          <div className="ml-auto flex items-center gap-2">
            <CountdownClock className="!ml-0" />
            <div className="hidden sm:flex items-center gap-2">
              <DarkModeSwitchButton />
              <Link
                to={exitTo}
                className="p-2 rounded-md text-text-muted hover:bg-feedback-error/10 hover:text-feedback-error transition-colors"
                aria-label="Leave simulation"
                title="Leave simulation"
              >
                <LogOut className="w-4 h-4" />
              </Link>
            </div>
            <button
              aria-label={isOpen ? 'close menu' : 'open menu'}
              onClick={() => setIsOpen(!isOpen)}
              className="sm:hidden p-2 border border-border-default rounded-md hover:bg-bg-subtle transition-colors"
            >
              {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile dropdown — backdrop + menu use z-(--z-navbar) to sit above framer-motion stacking contexts */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 top-14 z-(--z-navbar) sm:hidden"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />
          <nav className="fixed top-14 left-0 right-0 z-(--z-navbar) sm:hidden bg-bg-elevated border-b border-border-muted shadow-md">
            {items.map((item) => (
              <MobileNavItemLink key={item.to} item={item} onClick={() => setIsOpen(false)} />
            ))}
            <div className="border-t border-border-muted px-4 py-2 flex items-center justify-between">
              <DarkModeSwitchButton />
              <Link
                to={exitTo}
                onClick={() => setIsOpen(false)}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm text-text-muted hover:bg-feedback-error/10 hover:text-feedback-error transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Leave
              </Link>
            </div>
          </nav>
        </>
      )}
    </>
  );
}
